import { SHOT_MAX_SECONDS, SHOT_MIN_SECONDS, type CharacterDto, type ProjectDto } from '@studio/shared';
import { Clapperboard, FileText } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { ProjectSettingsCard } from '@/components/project/ProjectSettingsCard';
import { useProjectCall } from '@/components/project/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ConfirmDialog, SwitchRow } from '@/components/ui/controls';
import { Textarea } from '@/components/ui/form';

/** One scene of the script, as it becomes a shot. */
export interface ScriptScene {
  label: string;
  seconds: number;
  /** The scene in the writer's own words, without its dialogue lines. */
  picture: string;
  lines: { characterId: string | null; speakerName: string; text: string }[];
  characterIds: string[];
  withProduct: boolean;
}

const HEADING = /^\s*(?:scene|सीन|दृश्य|দৃশ্য)\s*(\d+)?\s*(?:\(\s*(\d{1,2})\s*(?:s|sec|secs|seconds?|सेकंड)?\s*\))?\s*[:.\-–—]?\s*(.*)$/i;
/** "Priya: "…"", "Friend – …": a short speaker name, then the words (quotes optional when the name is a character). */
const SPOKEN = /^\s*([^:"“”\n]{1,40}?)\s*[:：\-–—]\s*["“](.+?)["”]?\s*$|^\s*([^:"“”\n]{1,40}?)\s*[:：]\s*(.+)$/;
const clampSeconds = (n: number) => Math.min(SHOT_MAX_SECONDS, Math.max(SHOT_MIN_SECONDS, Math.round(n)));

/**
 * The whole video as the team writes it — scenes started with "SCENE 1:", "SCENE 2 (10s):", dialogues as
 * "Name: "words"" — cut into shots. Nothing is rewritten: each scene's text becomes its shot's picture text, its
 * dialogue lines the shot's spoken lines, and the project's characters (and product) named in it go along with their
 * pictures, so faces stay the same from shot to shot. No heading at all: the whole text is one shot.
 */
export function parseScript(script: string, cast: CharacterDto[], productTitle: string | null): ScriptScene[] {
  const blocks: { label: string; seconds: number | null; body: string[] }[] = [];
  for (const raw of script.replace(/\r/g, '').split('\n')) {
    const h = HEADING.exec(raw);
    if (h) {
      blocks.push({ label: `Scene ${h[1] ?? blocks.length + 1}`, seconds: h[2] ? Number(h[2]) : null, body: h[3]?.trim() ? [h[3].trim()] : [] });
      continue;
    }
    if (!blocks.length) blocks.push({ label: 'Scene 1', seconds: null, body: [] });
    blocks.at(-1)!.body.push(raw);
  }
  const named = (who: string) => {
    const w = who.trim().toLowerCase();
    return cast.find((c) => [c.name, c.callName].some((n) => n && n.trim().toLowerCase() === w)) ?? null;
  };
  const productWords = (productTitle ?? '')
    .split(/[\s\-–,()]+/)
    .filter((w) => w.length >= 4 && !/^(pack|month|tablet|herbal|with)$/i.test(w))
    .map((w) => w.toLowerCase());
  return blocks
    .map((b) => {
      const lines: ScriptScene['lines'] = [];
      const picture: string[] = [];
      for (const l of b.body) {
        const m = SPOKEN.exec(l);
        const who = (m?.[1] ?? m?.[3] ?? '').trim();
        const said = (m?.[2] ?? m?.[4] ?? '').trim();
        const character = who ? named(who) : null;
        // A spoken line: the words are in quotes, or the speaker is one of the project's characters.
        if (m && said && who.split(/\s+/).length <= 3 && (m[2] !== undefined || character)) {
          lines.push({ characterId: character?.id ?? null, speakerName: character ? '' : who, text: said.replace(/^["“]|["”]$/g, '') });
        } else picture.push(l);
      }
      const text = picture.join('\n').trim();
      const all = `${text}\n${lines.map((l) => l.text).join('\n')}`.toLowerCase();
      const characterIds = cast
        .filter((c) => lines.some((l) => l.characterId === c.id) || [c.name, c.callName].some((n) => n && n.trim().length >= 2 && all.includes(n.trim().toLowerCase())))
        .map((c) => c.id);
      const words = lines.reduce((n, l) => n + l.text.split(/\s+/).filter(Boolean).length, 0);
      return {
        label: b.label,
        seconds: clampSeconds(b.seconds ?? (words ? words / 2.5 + 3 : 8)),
        picture: text,
        lines,
        characterIds,
        withProduct: productWords.some((w) => all.includes(w)) || /\b(product|bottle|pack|packet|box)\b|प्रोडक्ट|পণ্য/i.test(all),
      };
    })
    .filter((s) => s.picture || s.lines.length);
}

const EXAMPLE = `SCENE 1 (8s): A small kitchen in the morning. Priya, 30s, looks worried at her reflection in a steel almirah mirror.
Priya: "इतना वज़न कैसे कम करूँ?"

SCENE 2 (10s): Her friend Neha walks in and hands her a pack of Fat Cutter Green Tea Tablet, smiling.
Neha: "ये ले, मुझे तीन महीने में फ़र्क दिखा।"

SCENE 3 (6s): Close-up of the Fat Cutter pack on the kitchen table, soft morning light.`;

const draftKey = (id: string) => `studio:script:${id}`;

/** Step 1: the whole video in one text — scenes, what we see, who says what — made into shots in one click. */
export function ScriptTab({ project, onMade }: { project: ProjectDto; onMade: () => void }) {
  const call = useProjectCall(project.id);
  const [script, setScript] = useState(() => {
    try {
      return localStorage.getItem(draftKey(project.id)) ?? '';
    } catch {
      return '';
    }
  });
  const [replace, setReplace] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    try {
      localStorage.setItem(draftKey(project.id), script);
    } catch {
      /* a private window: the draft is just not kept */
    }
  }, [project.id, script]);
  const scenes = useMemo(() => parseScript(script, project.cast, project.product?.title ?? null), [script, project.cast, project.product]);
  const nameOf = (id: string) => project.cast.find((c) => c.id === id)?.name ?? '?';
  const existing = project.shots.length;

  async function make() {
    setBusy(true);
    try {
      if (replace) for (const s of project.shots) if (!(await call(`/api/projects/shots/${s.id}`, { method: 'DELETE' }))) return;
      for (const s of scenes) {
        const data = {
          label: s.label,
          seconds: s.seconds,
          picture: s.picture,
          cast: [
            ...s.characterIds.map((id) => ({ type: 'character', id })),
            ...(s.withProduct && project.productId ? [{ type: 'product', id: project.productId }] : []),
          ],
          lines: s.lines.map((l, i) => ({ id: `l${i + 1}`, characterId: l.characterId, speakerName: l.speakerName, text: l.text })),
        };
        if (!(await call(`/api/projects/${project.id}/shots`, { method: 'POST', body: { kind: 'shot', data } }))) return;
      }
      onMade();
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_340px]">
      <div className="space-y-3">
        <Card className="space-y-3 p-4">
          <div className="flex items-center gap-2 font-medium">
            <FileText className="size-4" /> Write the whole video
          </div>
          <p className="text-sm text-muted-foreground">
            Start each scene with <b>SCENE 1:</b>, <b>SCENE 2:</b>… (add the length if you want, e.g. <b>SCENE 2 (10s):</b>,
            4–15 seconds). Describe what we see in your own words, and put spoken lines on their own line as{' '}
            <b>Name: "words"</b>. Each scene becomes one shot, exactly as written; characters and the product you name
            go along with their pictures. No headings: the whole text is one shot.
          </p>
          <Textarea
            value={script}
            onChange={(e) => setScript(e.target.value)}
            rows={18}
            placeholder={EXAMPLE}
            className="font-mono text-sm"
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setScript(EXAMPLE)} disabled={!!script.trim()}>
              Use the example
            </Button>
            <span className="text-xs text-muted-foreground">{script.length.toLocaleString()} characters · kept in this browser until you make the shots</span>
          </div>
        </Card>
        {!!scenes.length && (
          <Card className="space-y-2 p-4">
            <div className="font-medium">
              {scenes.length} {scenes.length === 1 ? 'shot' : 'shots'} · about {scenes.reduce((n, s) => n + s.seconds, 0)} s
            </div>
            {scenes.map((s, i) => (
              <div key={i} className="rounded-md border p-2 text-sm">
                <div className="flex flex-wrap items-center gap-1.5">
                  <b>{s.label}</b>
                  <Badge variant="secondary">{s.seconds} s</Badge>
                  {s.characterIds.map((id) => (
                    <Badge key={id} variant="outline">
                      {nameOf(id)}
                    </Badge>
                  ))}
                  {s.withProduct && project.productId && <Badge variant="outline">{project.product?.title ?? 'product'}</Badge>}
                  {!!s.lines.length && <Badge variant="outline">{s.lines.length} spoken</Badge>}
                </div>
                <p className="mt-1 line-clamp-2 text-muted-foreground">{s.picture}</p>
                {s.lines.map((l, j) => (
                  <p key={j} className="text-xs">
                    <b>{l.characterId ? nameOf(l.characterId) : l.speakerName}:</b> {l.text}
                  </p>
                ))}
              </div>
            ))}
            {existing > 0 && (
              <SwitchRow
                label={`Replace the ${existing} shot${existing === 1 ? '' : 's'} already in this project`}
                checked={replace}
                onCheckedChange={setReplace}
              />
            )}
            <Button onClick={() => (existing && replace ? setConfirming(true) : void make())} loading={busy}>
              <Clapperboard /> Make {scenes.length} {scenes.length === 1 ? 'shot' : 'shots'}
            </Button>
          </Card>
        )}
      </div>
      <ProjectSettingsCard project={project} />
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={`Replace the ${existing} shots?`}
        description="The shots now in this project, and their videos, are removed and the shots of this script are made instead."
        confirmLabel="Replace"
        destructive
        loading={busy}
        onConfirm={() => void make()}
      />
    </div>
  );
}
